/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import {
  Card,
  ListGroup,
  Button,
  Badge,
  Row,
  Col,
  Form,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFolderOpen,
  faExpandAlt,
  faCompressAlt,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import ContextMenu from 'components/global/ContextMenu';
import { formatFileSize, getFileIcon } from 'components/global/FileExplorer/fileExplorer.helpers';
import TableLoader from 'components/global/SignalTable/_components/TableLoader';
import { handleCreateFolder } from 'components/views/FounderVault/_helpers/founderVault.events';

const FileExplorer = ({
  isLoading,
  folders,
  onUploadClick,
  onClickEditFolder,
  onClickDeleteFolder,
  onClickEditDocument,
  onClickViewDocument,
  onClickDeleteDocument,
  onClickCreateFolder,
  mode = 'edit',
  initialExpandedFolders = {},
}) => {
  const [expandedFolders, setExpandedFolders] = useState(initialExpandedFolders);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleFolder = (folderId) => {
    setExpandedFolders({
      ...expandedFolders,
      [folderId]: !expandedFolders[folderId],
    });
  };

  const expandAll = () => {
    const expandedState = {};
    folders.forEach(folder => {
      expandedState[folder.id] = true;
    });
    setExpandedFolders(expandedState);
  };

  const collapseAll = () => {
    setExpandedFolders({});
  };

  const handleSearch = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    // If starting a new search, expand all folders
    if (searchTerm === '' && newSearchTerm !== '') {
      const allExpanded = {};
      folders.forEach(folder => {
        allExpanded[folder.id] = true;
      });
      setExpandedFolders(allExpanded);
    } else if (searchTerm !== '' && newSearchTerm === '') {
      // If clearing the search, collapse all folders
      setExpandedFolders({});
    }
  };

  // Ensure folders with matching documents stay expanded during search
  useEffect(() => {
    if (searchTerm) {
      const newExpandedState = { ...expandedFolders };
      folders.forEach(folder => {
        if (folder.documents?.length > 0) {
          newExpandedState[folder.id] = true;
        }
      });
      setExpandedFolders(newExpandedState);
    }
  }, [searchTerm]);

  // useEffect(() => {
  //   setExpandedFolders(initialExpandedFolders);
  // }, [initialExpandedFolders]);

  const getFolderContextMenuItems = (folder) => {
    if (mode !== 'edit') {
      return [];
    }
    return [
      { label: 'Rename', onClick: () => onClickEditFolder(folder) },
      { label: 'Delete', onClick: () => onClickDeleteFolder(folder) },
    ];
  };

  const getDocContextMenuItems = (doc) => {
    const items = [
      { label: 'View', onClick: () => onClickViewDocument(doc) },
    ];
    if (mode === 'edit') {
      items.push({ label: 'Edit/Move', onClick: () => onClickEditDocument(doc) });
      items.push({ label: 'Delete', onClick: () => onClickDeleteDocument(doc) });
    }
    return items;
  };

  const getFilteredDocs = (docs) => {
    if (searchTerm) {
      return docs?.filter(doc => doc.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    }
    return docs?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  return (
    <>
      <Row className="mb-32 pb-16 align-items-center border-bottom border-gray-700">
        <Col>
          <Form.Control
            placeholder="Search documents by name..."
            value={searchTerm}
            onChange={handleSearch}
            className="rounded border-0 bg-gray-700"
          />
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
        {/* Add Folder Button */}
        <Button
          variant="transparent"
          onClick={() => onClickCreateFolder()}
          className="me-16"
        >
          <FontAwesomeIcon icon={faFolderOpen} className="me-8" size="2xs" />
          <small className="ms-8 text-white">Add Folder</small>
        </Button>
          <Button variant="transparent" onClick={expandAll}>
            <FontAwesomeIcon icon={faExpandAlt} className="me-8" size="2xs" />
            <small className="ms-8 text-white">Expand All</small>
          </Button>
          <Button variant="transparent" onClick={collapseAll}>
            <FontAwesomeIcon icon={faCompressAlt} className="me-8" size="2xs" />
            <small className="ms-8 text-white">Collapse All</small>
          </Button>
          {mode === 'edit' && (
            <Button variant="primary" className="me-16" size="sm" onClick={onUploadClick}>
              <FontAwesomeIcon icon={faUpload} className="me-8" size="2xs" />
              <span className="text-white">Upload Document</span>
            </Button>
          )}
        </Col>
      </Row>
      {isLoading ? (
        <div className="text-center p-32 bg-transparent border-0">
          <TableLoader length={3} />
        </div>
      ) : (
        folders.length === 0 ? (
          <Card className="text-center p-32 bg-transparent border-0">
            <Card.Body>
              <p className="mb-0">No folders available</p>
            </Card.Body>
          </Card>
        ) : (
          <div>
            {folders.map((folder) => (
              <Card key={folder.id} className="mb-24 bg-transparent border-0">
                <Card.Header
                  className="d-flex align-items-center cursor-pointer bg-gray-800 border-0 "
                  onClick={() => toggleFolder(folder.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon
                    icon={expandedFolders[folder.id] ? faFolderOpen : faFolder}
                    className="me-16 text-white"
                  />
                  <span className="me-auto text-white">{folder.name}</span>
                  <Badge bg="secondary" className="me-16 text-white">
                    {folder.documents?.length || 0} files
                  </Badge>
                  {mode === 'edit' && (
                    <ContextMenu
                      menuItems={getFolderContextMenuItems(folder)}
                    />
                  )}
                </Card.Header>
                <Card.Body
                  className="bg-gray-700 border-bottom border-gray-800"
                  style={{ display: expandedFolders[folder.id] ? 'block' : 'none' }}
                >
                  {getFilteredDocs(folder.documents)?.length > 0 ? (
                    <ListGroup variant="flush">
                      {getFilteredDocs(folder.documents).map((doc, idx) => (
                        <ListGroup.Item 
                          key={doc.id || idx.toString()} 
                          className="py-8 bg-transparent border-bottom border-gray-700"
                        >
                          <div className="d-flex align-items-center gap-8">
                            {/* File Icon */}
                            <div className="flex-shrink-0">
                              <FontAwesomeIcon
                                icon={getFileIcon(doc.type)}
                                className="text-info"
                                size="lg"
                              />
                            </div>
                            
                            {/* File Info - Takes up remaining space */}
                            <div className="flex-grow-1 min-w-0">
                              <p className="mb-0 fw-bold text-truncate">{doc.name}</p>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <small className="text-info">
                                  {moment(doc.updatedAt).format('MMM D, YYYY')}
                                </small>
                                {doc.size && (
                                  <>
                                    <span className="text-muted">•</span>
                                    <small className="text-info">
                                      {formatFileSize(doc.size)}
                                    </small>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {/* Context Menu */}
                            <div className="flex-shrink-0">
                              <ContextMenu
                                menuItems={getDocContextMenuItems(doc, mode)}
                                align="end"
                              />
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <p className="text-center text-white mb-0">
                      {searchTerm ? (
                        <span>
                          No documents matching
                          <span className="text-info fw-bold">
                            {' "'}
                            {searchTerm}
                            {'" '}
                          </span>
                          in this folder
                        </span>
                      ) : 'No documents in this folder'}
                    </p>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        )
      )}
    </>
  );
};

export default FileExplorer;
